import { useCallback, useEffect, useMemo, useState } from "react";

import api from "@helpers/api";

export const WALLET_PROVIDERS = {
  ASAAS: "asaas",
  STRIPE: "stripe",
};

const PROVIDER_DEFAULTS = {
  [WALLET_PROVIDERS.ASAAS]: {
    currency: "BRL",
    label: "Carteira BRL",
  },
  [WALLET_PROVIDERS.STRIPE]: {
    currency: "USD",
    label: "Carteira Internacional",
  },
};

// GET /wallets/list returns every connected wallet (all providers) as a bare
// array of full wallet objects — the dual-wallet source of truth. The legacy
// GET /wallet overview stays as the fallback for older backends. (The previous
// primary, GET /wallet/status, never existed in core — every call 404'd into
// the fallback.)
const WALLET_LIST_ENDPOINT = "wallets/list";
const WALLET_LEGACY_ENDPOINT = "wallet";

function readProvider(rawProvider, fallback = WALLET_PROVIDERS.ASAAS) {
  const value =
    rawProvider && typeof rawProvider === "object"
      ? rawProvider.code || rawProvider.provider || rawProvider.name || fallback
      : rawProvider || fallback;

  return String(value || fallback).toLowerCase();
}

function normalizeCurrency(rawCurrency, provider) {
  return String(
    rawCurrency || PROVIDER_DEFAULTS[provider]?.currency || "BRL"
  ).toUpperCase();
}

function readBool(...values) {
  const value = values.find(v => v !== undefined && v !== null);
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["1", "true", "yes", "enabled", "approved", "active"].includes(
      value.toLowerCase()
    );
  }
  return false;
}

function isApiError(response) {
  return !!response?.isAxiosError || !!response?.response;
}

function addWalletCandidate(candidates, rawWallet, fallbackProvider) {
  if (!rawWallet) return;

  if (Array.isArray(rawWallet)) {
    rawWallet.forEach(wallet => addWalletCandidate(candidates, wallet, fallbackProvider));
    return;
  }

  candidates.push({
    wallet: rawWallet,
    fallbackProvider: readProvider(fallbackProvider || rawWallet?.provider),
  });
}

function addProviderObject(candidates, source) {
  if (!source || Array.isArray(source) || typeof source !== "object") return;

  addWalletCandidate(candidates, source[WALLET_PROVIDERS.ASAAS], WALLET_PROVIDERS.ASAAS);
  addWalletCandidate(candidates, source[WALLET_PROVIDERS.STRIPE], WALLET_PROVIDERS.STRIPE);
  addWalletCandidate(candidates, source.national, WALLET_PROVIDERS.ASAAS);
  addWalletCandidate(candidates, source.brazil, WALLET_PROVIDERS.ASAAS);
  addWalletCandidate(candidates, source.local, WALLET_PROVIDERS.ASAAS);
  addWalletCandidate(candidates, source.global, WALLET_PROVIDERS.STRIPE);
  addWalletCandidate(candidates, source.international, WALLET_PROVIDERS.STRIPE);
}

function collectWalletCandidates(response) {
  const candidates = [];

  if (Array.isArray(response)) {
    addWalletCandidate(candidates, response, null);
  }

  if (Array.isArray(response?.wallets)) {
    addWalletCandidate(candidates, response.wallets, null);
  } else {
    addProviderObject(candidates, response?.wallets);
  }

  addWalletCandidate(candidates, response?.wallet, null);
  addWalletCandidate(candidates, response?.account, null);
  addWalletCandidate(candidates, response?.accounts, null);
  addProviderObject(candidates, response);
  addProviderObject(candidates, response?.providers);
  addProviderObject(candidates, response?.walletStatuses);
  addProviderObject(candidates, response?.wallet_statuses);
  addProviderObject(candidates, response?.statuses);

  return candidates;
}

function unwrapWalletPayload(rawWallet) {
  if (!rawWallet || Array.isArray(rawWallet) || typeof rawWallet !== "object") {
    return rawWallet;
  }

  const nestedWallet = rawWallet.wallet || rawWallet.account;
  if (!nestedWallet || Array.isArray(nestedWallet) || typeof nestedWallet !== "object") {
    return rawWallet;
  }

  return {
    ...rawWallet,
    ...nestedWallet,
    provider: nestedWallet.provider || rawWallet.provider,
    statuses: nestedWallet.statuses || rawWallet.statuses || rawWallet.status,
    balance: nestedWallet.balance || rawWallet.balance,
    onboarding: nestedWallet.onboarding || rawWallet.onboarding || rawWallet.connect,
  };
}

export function emptyWallet(provider) {
  const normalizedProvider = readProvider(provider);
  const currency = normalizeCurrency(null, normalizedProvider);

  return {
    id: null,
    provider: normalizedProvider,
    label: PROVIDER_DEFAULTS[normalizedProvider]?.label || normalizedProvider,
    currency,
    balance: {
      available: 0,
      future: 0,
      paid: 0,
    },
    statuses: {
      general: "not_created",
    },
    taxes: normalizedProvider === WALLET_PROVIDERS.STRIPE
      ? { percentile: 9.49, fixed: 199, anticipation: null }
      : null,
    withdrawal: null,
    onboarding: {
      detailsSubmitted: false,
      chargesEnabled: false,
      payoutsEnabled: false,
      requirements: [],
    },
    connected: false,
    missing: true,
    raw: null,
  };
}

export function normalizeWallet(rawWallet, fallbackProvider = WALLET_PROVIDERS.ASAAS) {
  if (!rawWallet) return emptyWallet(fallbackProvider);

  rawWallet = unwrapWalletPayload(rawWallet);

  const provider = readProvider(rawWallet.provider, fallbackProvider);
  const currency = normalizeCurrency(rawWallet.currency, provider);
  const rawStatuses = rawWallet.statuses || {};
  const statuses =
    rawStatuses && typeof rawStatuses === "object" && !Array.isArray(rawStatuses)
      ? rawStatuses
      : {};
  const onboarding =
    (rawWallet.onboarding && typeof rawWallet.onboarding === "object" ? rawWallet.onboarding : null) ||
    (rawWallet.connect && typeof rawWallet.connect === "object" ? rawWallet.connect : null) ||
    {};
  // wallets/list nests the Stripe capability flags inside the provider
  // object (provider.charges_enabled, …) — read them from there too.
  const providerObject =
    rawWallet.provider && typeof rawWallet.provider === "object" ? rawWallet.provider : {};
  const rawStatus =
    rawWallet.status && typeof rawWallet.status === "object"
      ? rawWallet.status.general
      : rawWallet.status;
  const requirements =
    onboarding.requirements ||
    rawWallet.requirements ||
    rawWallet.provider?.requirements ||
    [];

  const detailsSubmitted = readBool(
    onboarding.detailsSubmitted,
    onboarding.details_submitted,
    providerObject.detailsSubmitted,
    providerObject.details_submitted,
    rawWallet.detailsSubmitted,
    rawWallet.details_submitted,
    statuses.detailsSubmitted,
    statuses.details_submitted
  );
  const chargesEnabled = readBool(
    onboarding.chargesEnabled,
    onboarding.charges_enabled,
    providerObject.chargesEnabled,
    providerObject.charges_enabled,
    rawWallet.chargesEnabled,
    rawWallet.charges_enabled,
    statuses.chargesEnabled,
    statuses.charges_enabled,
    statuses.charges
  );
  const payoutsEnabled = readBool(
    onboarding.payoutsEnabled,
    onboarding.payouts_enabled,
    providerObject.payoutsEnabled,
    providerObject.payouts_enabled,
    rawWallet.payoutsEnabled,
    rawWallet.payouts_enabled,
    statuses.payoutsEnabled,
    statuses.payouts_enabled,
    statuses.payouts
  );

  const generalStatus =
    statuses.general ||
    rawStatus ||
    (chargesEnabled && payoutsEnabled ? "approved" : null);

  // Stripe: capability flags first, but an approved general status (the
  // webhook-reconciled state the backend charges against) also counts —
  // mirrors core's Wallet::stripeChargeable() predicate.
  const statusApproved = ["approved", "active"].includes(
    String(generalStatus || "").toLowerCase()
  );
  const connected =
    rawWallet.connected ??
    (provider === WALLET_PROVIDERS.STRIPE
      ? (chargesEnabled && payoutsEnabled) || statusApproved
      : statusApproved);

  return {
    id: rawWallet.id || null,
    provider,
    label: PROVIDER_DEFAULTS[provider]?.label || provider,
    currency,
    balance: {
      ...(rawWallet.balance || {}),
      available: rawWallet.balance?.available ?? rawWallet.available ?? 0,
    },
    statuses: {
      ...statuses,
      general: generalStatus || statuses.general || "not_created",
    },
    taxes: rawWallet.taxes || (provider === WALLET_PROVIDERS.STRIPE
      ? { percentile: 9.49, fixed: 199, anticipation: null }
      : null),
    withdrawal: rawWallet.withdrawal || null,
    onboarding: {
      detailsSubmitted,
      chargesEnabled,
      payoutsEnabled,
      requirements,
      dashboardUrl:
        onboarding.dashboardUrl ||
        onboarding.dashboard_url ||
        providerObject.dashboard_url ||
        null,
      externalAccountLast4:
        onboarding.externalAccountLast4 ||
        onboarding.external_account_last4 ||
        providerObject.external_account_last4 ||
        rawWallet.externalAccountLast4 ||
        null,
    },
    connected: !!connected,
    missing: !rawWallet.id && generalStatus === "not_created",
    raw: rawWallet,
  };
}

export function normalizeWalletResponse(response) {
  const candidates = collectWalletCandidates(response)
    .filter(({ wallet }) => {
      if (!wallet || Array.isArray(wallet) || typeof wallet !== "object") return false;

      return !!(
        wallet.id ||
        wallet.wallet ||
        wallet.account ||
        wallet.balance ||
        wallet.statuses ||
        wallet.withdrawal ||
        wallet.taxes ||
        wallet.status ||
        wallet.provider ||
        wallet.connected !== undefined ||
        wallet.onboarding ||
        wallet.connect ||
        wallet.charges_enabled !== undefined ||
        wallet.chargesEnabled !== undefined ||
        wallet.payouts_enabled !== undefined ||
        wallet.payoutsEnabled !== undefined ||
        wallet.details_submitted !== undefined ||
        wallet.detailsSubmitted !== undefined
      );
    });

  const walletMap = new Map();

  candidates.forEach(({ wallet, fallbackProvider }) => {
    const normalizedWallet = normalizeWallet(wallet, fallbackProvider);
    walletMap.set(normalizedWallet.provider, normalizedWallet);
  });

  const wallets = [...walletMap.values()];

  if (!wallets.length && response) {
    const looksLikeLegacyWallet =
      response.id ||
      response.balance ||
      response.statuses ||
      response.withdrawal ||
      response.taxes ||
      response.status;

    if (looksLikeLegacyWallet) {
      wallets.push(normalizeWallet(response, WALLET_PROVIDERS.ASAAS));
    }
  }

  return wallets;
}

export function getWalletByProvider(wallets, provider) {
  const normalizedProvider = readProvider(provider);
  return (
    wallets.find(wallet => wallet.provider === normalizedProvider) ||
    emptyWallet(normalizedProvider)
  );
}

export function walletIsConnected(wallet) {
  return !!wallet?.connected;
}

export function walletDisplayName(provider) {
  const normalizedProvider = readProvider(provider);
  return PROVIDER_DEFAULTS[normalizedProvider]?.label || normalizedProvider;
}

// Stripe Express has no in-app withdraw — the number creators track is the
// payments balance (available + pending), the same headline the Stripe
// dashboard shows. Asaas keeps the withdrawable (available) amount.
export function walletHeadlineBalance(wallet) {
  const available = parseInt(wallet?.balance?.available, 10) || 0;

  if (wallet?.provider !== WALLET_PROVIDERS.STRIPE) return available;

  return available + (parseInt(wallet?.balance?.pending, 10) || 0);
}

// Funds sit in the account's settlement currency (a BR Stripe connected
// account holds BRL even though the wallet row says USD).
export function walletBalanceCurrency(wallet, fallback = "BRL") {
  return wallet?.balance?.currency || wallet?.currency || fallback;
}

export function walletCanWithdraw(wallet) {
  if (!walletIsConnected(wallet)) return false;

  return wallet?.provider === WALLET_PROVIDERS.STRIPE
    ? wallet?.balance?.available > 0
    : wallet?.balance?.available >= 500;
}

export function walletWithdrawalPath(wallet) {
  // asaas is the URL default — only the stripe selection needs the param.
  return wallet?.provider === WALLET_PROVIDERS.STRIPE
    ? "/banking/withdrawal?provider=stripe"
    : "/banking/withdrawal/request";
}

export async function fetchWallets() {
  let response = await api.get(WALLET_LIST_ENDPOINT);

  if (isApiError(response) || !response?.data) {
    response = await api.get(WALLET_LEGACY_ENDPOINT);
  }

  const data = response?.data;
  const wallets = normalizeWalletResponse(data);

  return {
    raw: data,
    wallets,
    asaas: getWalletByProvider(wallets, WALLET_PROVIDERS.ASAAS),
    stripe: getWalletByProvider(wallets, WALLET_PROVIDERS.STRIPE),
  };
}

export function useWallets({ enabled = true } = {}) {
  const [rawWallets, setRawWallets] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);

    try {
      const result = await fetchWallets();
      setRawResponse(result.raw);
      setRawWallets(result.wallets);
    } catch (e) {
      setError(e);
      setRawWallets([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    refresh();
  }, [enabled, refresh]);

  const asaas = useMemo(
    () => getWalletByProvider(rawWallets, WALLET_PROVIDERS.ASAAS),
    [rawWallets]
  );
  const stripe = useMemo(
    () => getWalletByProvider(rawWallets, WALLET_PROVIDERS.STRIPE),
    [rawWallets]
  );
  const hasAnyConnected = rawWallets.some(walletIsConnected);

  return {
    raw: rawResponse,
    wallets: rawWallets,
    asaas,
    stripe,
    loading,
    error,
    refresh,
    getWallet: provider => getWalletByProvider(rawWallets, provider),
    hasAnyConnected,
  };
}

// Kept for older callers, but backed by the combined wallet status request.
export async function fetchStripeConnectStatus() {
  const { stripe } = await fetchWallets();
  if (!stripe || stripe.missing) return null;

  return {
    provider: stripe.provider || WALLET_PROVIDERS.STRIPE,
    currency: normalizeCurrency(stripe.currency, WALLET_PROVIDERS.STRIPE),
    detailsSubmitted: stripe.onboarding?.detailsSubmitted,
    chargesEnabled: stripe.onboarding?.chargesEnabled,
    payoutsEnabled: stripe.onboarding?.payoutsEnabled,
    requirements: stripe.onboarding?.requirements || [],
    externalAccountLast4: stripe.onboarding?.externalAccountLast4 || null,
    dashboardUrl: stripe.onboarding?.dashboardUrl || null,
  };
}

// The backend infers the link kind from the wallet state: no wallet → creates
// the Connect account + onboarding link; incomplete → new onboarding link;
// approved → Express dashboard login link. No request body is needed.
export async function createStripeConnectLink() {
  const { data } = await api.post("wallet/connect/stripe");

  return {
    url: data.url,
    provider: data.provider || WALLET_PROVIDERS.STRIPE,
    currency: normalizeCurrency(data.currency, WALLET_PROVIDERS.STRIPE),
    expiresAt: data.expires_at || data.expiresAt || null,
  };
}
